import React from "react";

import { RoleTypeExtension } from "../../constants";
import { useDisableBodyScroll } from "../../hooks/useDisableBodyScroll";
import type { GeneralUserData } from "../../types/generalUserManage";
import { formatLastActive } from "../../utils/generalUserManage";

import UserDashboardBox from "./UserDashboardBox";

interface UserDetailModalProps {
  onClose: () => void;
  userData: GeneralUserData;
  onEdit: () => void;
  isViewer?: boolean;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  onClose,
  userData,
  onEdit,
  isViewer = false,
}) => {
  // モーダル表示時に背景のスクロールを無効化
  useDisableBodyScroll();

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center">
      {/* 背景オーバーレイ */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      />

      {/* モーダル本体 */}
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-3xl">
        {/* タイトルバー */}
        <div className="bg-[#5571ad] text-white px-6 py-3 rounded-t-lg">
          <h3 className="text-2xl font-bold">ユーザー詳細情報</h3>
        </div>

        {/* 詳細情報 */}
        <div className="p-6">
          {/* セクションタイトル */}
          <h4 className="text-lg font-bold mb-4">基本情報</h4>

          {/* 情報テーブル（2列） */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* 左列 */}
            <div className="space-y-4">
              {/* No */}
              <div>
                <div className="text-gray-900 font-bold mb-1">No</div>
                <div className="text-gray-900">
                  {userData.employee_id
                    ? String(userData.employee_id).padStart(5, "0")
                    : "-"}
                </div>
              </div>

              {/* ユーザ名 */}
              <div>
                <div className="text-gray-900 font-bold mb-1">ユーザ名</div>
                <div className="text-gray-900">{userData.full_name || "-"}</div>
              </div>

              {/* メールアドレス */}
              <div>
                <div className="text-gray-900 font-bold mb-1">
                  メールアドレス
                </div>
                <div className="text-gray-900">{userData.email || "-"}</div>
              </div>
            </div>

            {/* 右列 */}
            <div className="space-y-4">
              {/* ログインID */}
              <div>
                <div className="text-gray-900 font-bold mb-1">ログインID</div>
                <div className="text-gray-900">{userData.user_name || "-"}</div>
              </div>

              {/* 権限 */}
              <div>
                <div className="text-gray-900 font-bold mb-1">権限</div>
                <div className="text-gray-900">
                  {RoleTypeExtension.isAdmin(userData.role_type) ? (
                    <span className="px-2 py-1 text-xs text-white bg-orange-500 rounded-full inline-block">
                      {RoleTypeExtension.getLabel(userData.role_type)}
                    </span>
                  ) : RoleTypeExtension.isViewer(userData.role_type) ? (
                    <span className="px-2 py-1 text-xs text-white bg-blue-600 rounded-full inline-block">
                      {RoleTypeExtension.getLabel(userData.role_type)}
                    </span>
                  ) : userData.role_type ? (
                    RoleTypeExtension.getLabel(userData.role_type)
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              {/* 最終更新時刻 */}
              <div>
                <div className="text-gray-900 font-bold mb-1">最終更新時刻</div>
                <div className="text-gray-900">
                  {formatLastActive(userData.last_active_time)}
                </div>
              </div>
            </div>
          </div>

          {/* セクションタイトル */}
          <h4 className="text-lg font-bold mb-4">使用状況</h4>

          {/* ダッシュボックス */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <UserDashboardBox
              title="今月使用量"
              mainText={`${(userData.credit_consumed || 0).toLocaleString()}`}
              subText="クレジット"
              mainTextColor="#2563eb"
            />
            <UserDashboardBox
              title="個別上限(今月)"
              mainText={
                userData.credit_limit !== null
                  ? userData.credit_limit.toLocaleString()
                  : "上限なし"
              }
              subText={userData.credit_limit !== null ? "クレジット" : ""}
              mainTextColor="#16a34a"
            />
            <UserDashboardBox
              title="使用率"
              mainText={
                userData.credit_limit === null || userData.credit_limit === 0
                  ? "-"
                  : `${Math.round(((userData.credit_consumed || 0) / userData.credit_limit) * 100)}%`
              }
              subText={
                userData.credit_limit !== null && userData.credit_limit !== 0
                  ? "個別上限に対して"
                  : ""
              }
              mainTextColor="#f97316"
            />
          </div>
        </div>

        {/* ボトムバー */}
        <div className="flex justify-end gap-3 px-6 py-3 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            閉じる
          </button>
          <button
            type="button"
            onClick={onEdit}
            disabled={isViewer}
            className={`px-6 py-2 text-white rounded-md ${
              isViewer
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            編集
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
